/**
 * iter214.7 — WhatsApp threaded Conversation panel.
 *
 * WhatsApp-Web-style compact 2-pane view:
 *   • Left: list of conversations (phone + last preview + unread badge)
 *   • Right: threaded messages (green = outbound, gray = inbound) + composer
 *
 * Composer auto-detects Meta's 24-hour customer-service window:
 *   • Inside 24h → free-form text input
 *   • Outside 24h → template picker (from approved SplitEasy catalog)
 *
 * Used in `/whatsapp-settings` as the "Chat" tab.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  MessageCircle, Send, Loader2, RefreshCw, Clock, AlertTriangle,
  CheckCircle2, ChevronLeft, User,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useRealtime } from '../context/RealtimeContext';

function relTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export const WhatsAppConversationPanel = () => {
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedWaid, setSelectedWaid] = useState(null);
  const [thread, setThread] = useState(null); // { messages, can_free_form, ... }
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [templateType, setTemplateType] = useState('');
  const [templateParams, setTemplateParams] = useState({});
  const [templateCatalog, setTemplateCatalog] = useState([]);
  const [showMobileThread, setShowMobileThread] = useState(false);
  const messagesEndRef = useRef(null);
  const { onEvent } = useRealtime();
  const selectedWaidRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get('/whatsapp/conversations?limit=100');
      setConversations(r.data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load conversations');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (waid) => {
    if (!waid) return;
    setLoadingThread(true);
    try {
      const r = await api.get(`/whatsapp/conversations/${waid}?limit=200`);
      setThread(r.data);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 60);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load thread');
    } finally {
      setLoadingThread(false);
    }
  }, []);

  const loadTemplateCatalog = useCallback(async () => {
    try {
      const r = await api.get('/whatsapp/templates');
      // Merge catalog + Meta status so we can filter APPROVED-only templates.
      const catalog = r.data.catalog || [];
      const templates = r.data.templates || [];
      const statusByType = {};
      templates.forEach((t) => {
        if (t.splitease_notif_type) {
          statusByType[t.splitease_notif_type] = (t.status || '').toUpperCase();
        }
      });
      setTemplateCatalog(
        catalog.map((c) => ({
          ...c,
          status: statusByType[c.notif_type] || 'NOT_SUBMITTED',
        })),
      );
    } catch {
      // silent — catalog is optional
    }
  }, []);

  useEffect(() => {
    loadList();
    loadTemplateCatalog();
  }, [loadList, loadTemplateCatalog]);

  useEffect(() => {
    if (selectedWaid) loadThread(selectedWaid);
    selectedWaidRef.current = selectedWaid;
  }, [selectedWaid, loadThread]);

  // iter214.8 — Subscribe to SSE `wa_conversation_update` events for instant
  // updates. Replaces the 20-s poll. Refetches thread if the event is for the
  // currently-selected phone; always refreshes the sidebar list.
  useEffect(() => {
    const off = onEvent(['wa_conversation_update'], (_type, payload) => {
      const eventWaid = (payload?.waid || '').replace(/^\+/, '');
      loadList();
      if (eventWaid && eventWaid === selectedWaidRef.current) {
        loadThread(eventWaid);
      }
    });
    return off;
  }, [onEvent, loadList, loadThread]);

  const openConversation = (waid) => {
    setSelectedWaid(waid);
    setReplyText('');
    setTemplateType('');
    setTemplateParams({});
    setShowMobileThread(true);
  };

  const sendReply = async () => {
    if (!selectedWaid || !thread) return;
    setSending(true);
    try {
      if (thread.can_free_form) {
        if (!replyText.trim()) {
          toast.error('Type a message first');
          setSending(false);
          return;
        }
        await api.post(`/whatsapp/conversations/${selectedWaid}/reply`, {
          text: replyText.trim(),
          group_id: conversations.find((c) => c.waid === selectedWaid)?.group_ids?.[0],
        });
        toast.success('Message sent');
        setReplyText('');
      } else {
        if (!templateType) {
          toast.error('Pick a template');
          setSending(false);
          return;
        }
        const def = templateCatalog.find((t) => t.notif_type === templateType);
        const expected = def?.param_count || 0;
        const params = [];
        for (let i = 1; i <= expected; i += 1) {
          const v = (templateParams[i] || '').trim();
          if (!v) {
            toast.error(`Fill parameter #${i}`);
            setSending(false);
            return;
          }
          params.push(v);
        }
        await api.post(`/whatsapp/conversations/${selectedWaid}/reply`, {
          template_type: templateType,
          template_params: params,
          group_id: conversations.find((c) => c.waid === selectedWaid)?.group_ids?.[0],
        });
        toast.success('Template sent');
        setTemplateType('');
        setTemplateParams({});
      }
      // Refresh thread
      loadThread(selectedWaid);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const filteredCatalog = useMemo(
    () => templateCatalog.filter((t) => (t.status || '').toUpperCase() === 'APPROVED'),
    [templateCatalog],
  );

  return (
    <Card
      className="p-0 border-2 border-border/70 shadow-lg overflow-hidden"
      data-testid="wa-conversation-panel"
    >
      <div className="flex items-center justify-between gap-3 p-3 border-b border-border/60 bg-muted/30">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          <h3 className="text-sm font-bold">WhatsApp Chat</h3>
          <Badge variant="outline" data-testid="wa-conv-count">
            {conversations.length}
          </Badge>
          {/* iter214.8 — Live indicator (SSE-powered) */}
          <span
            className="flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300"
            title="Real-time updates via SSE"
            data-testid="wa-conv-live"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
            </span>
            live
          </span>
        </div>
        <Button
          onClick={loadList}
          disabled={loadingList}
          variant="outline"
          size="sm"
          data-testid="wa-conv-refresh"
        >
          {loadingList ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]" style={{ height: 520 }}>
        {/* Sidebar */}
        <aside
          className={`border-r border-border/60 bg-background overflow-y-auto ${
            showMobileThread ? 'hidden md:block' : ''
          }`}
          data-testid="wa-conv-sidebar"
        >
          {conversations.length === 0 && !loadingList && (
            <div className="p-4 text-center text-xs text-muted-foreground">
              No conversations yet. When members reply to WhatsApp, they will appear here.
            </div>
          )}
          {conversations.map((c) => {
            const active = c.waid === selectedWaid;
            return (
              <button
                key={c.waid}
                onClick={() => openConversation(c.waid)}
                data-testid={`wa-conv-item-${c.waid}`}
                className={`w-full text-left p-2.5 border-b border-border/40 hover:bg-muted/50 transition-colors ${
                  active ? 'bg-primary/10' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <User className="w-3 h-3 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-semibold truncate">
                      {c.display_name || c.phone}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {relTime(c.last_at)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <p className="text-xs text-muted-foreground truncate">
                    <span className="font-mono text-xs mr-1">
                      {c.last_direction === 'out' ? '→' : '←'}
                    </span>
                    {c.last_preview || '(no content)'}
                  </p>
                  {c.unread_count > 0 && (
                    <Badge
                      className="h-4 min-w-4 px-1 text-xs shrink-0"
                      data-testid={`wa-conv-unread-${c.waid}`}
                    >
                      {c.unread_count}
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </aside>

        {/* Thread */}
        <section
          className={`flex flex-col overflow-hidden ${
            showMobileThread ? '' : 'hidden md:flex'
          }`}
          data-testid="wa-conv-thread"
        >
          {!selectedWaid && (
            <div className="flex-1 flex items-center justify-center p-6 text-center text-sm text-muted-foreground">
              <div>
                <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>Pick a conversation to start chatting.</p>
              </div>
            </div>
          )}

          {selectedWaid && (
            <>
              {/* Thread header */}
              <div className="flex items-center gap-2 p-2.5 border-b border-border/60 bg-muted/20">
                <Button
                  onClick={() => setShowMobileThread(false)}
                  variant="ghost"
                  size="sm"
                  className="md:hidden"
                  data-testid="wa-conv-back"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">
                    {thread?.display_name || `+${selectedWaid}`}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    +{selectedWaid} · {thread?.count || 0} messages
                  </p>
                </div>
                {thread?.can_free_form ? (
                  <Badge
                    variant="outline"
                    className="text-xs bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                    data-testid="wa-conv-window-open"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    24h open
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="text-xs bg-amber-500/10 text-amber-700 border-amber-500/30"
                    data-testid="wa-conv-window-closed"
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    24h closed
                  </Badge>
                )}
              </div>

              {/* Messages */}
              <div
                className="flex-1 overflow-y-auto p-3 space-y-1.5 bg-gradient-to-b from-muted/10 to-muted/5"
                data-testid="wa-conv-messages"
              >
                {loadingThread && (
                  <div className="text-center text-xs text-muted-foreground py-6">
                    <Loader2 className="w-4 h-4 inline animate-spin" /> loading…
                  </div>
                )}
                {(thread?.messages || []).map((m) => (
                  <div
                    key={m.id || `${m.at}-${m.direction}`}
                    className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}
                    data-testid={`wa-conv-msg-${m.direction}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-[13px] shadow-sm ${
                        m.direction === 'out'
                          ? 'bg-emerald-500/90 text-white rounded-br-sm'
                          : 'bg-background border border-border/70 rounded-bl-sm'
                      }`}
                    >
                      {m.button_text && (
                        <span
                          className={`inline-block text-xs uppercase tracking-wide font-bold mr-1 px-1.5 py-0.5 rounded-full ${
                            m.direction === 'out'
                              ? 'bg-white/20'
                              : 'bg-primary/10 text-primary'
                          }`}
                        >
                          Button: {m.button_text}
                        </span>
                      )}
                      <span className="whitespace-pre-wrap break-words">
                        {m.text || (m.direction === 'out' && m.notif_type ? `[${m.notif_type}]` : '')}
                      </span>
                      <div
                        className={`flex items-center gap-1.5 mt-0.5 text-xs ${
                          m.direction === 'out' ? 'text-white/75' : 'text-muted-foreground'
                        }`}
                      >
                        <span>{fmtTime(m.at)}</span>
                        {m.direction === 'out' && m.status && (
                          <span className="uppercase tracking-wider">· {m.status}</span>
                        )}
                        {m.direction === 'out' && m.error_code && (
                          <span className="text-red-200">
                            · err {m.error_code}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              <div className="border-t border-border/60 bg-background p-2.5">
                {thread?.can_free_form ? (
                  <div className="flex gap-2 items-end" data-testid="wa-conv-composer-free">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                      placeholder="Type a reply… (Cmd/Ctrl-Enter to send)"
                      data-testid="wa-conv-reply-input"
                      className="text-sm resize-none rounded-xl min-h-[44px] max-h-32"
                      rows={1}
                    />
                    <Button
                      onClick={sendReply}
                      disabled={sending || !replyText.trim()}
                      data-testid="wa-conv-reply-send"
                      className="rounded-xl h-11 shrink-0"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="wa-conv-composer-template">
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>Outside 24h window — pick a template</span>
                    </div>
                    <select
                      value={templateType}
                      onChange={(e) => {
                        setTemplateType(e.target.value);
                        setTemplateParams({});
                      }}
                      data-testid="wa-conv-template-select"
                      className="w-full text-sm rounded-xl border-2 h-10 px-2 bg-background"
                    >
                      <option value="">— pick a template —</option>
                      {filteredCatalog.map((t) => (
                        <option key={t.notif_type} value={t.notif_type}>
                          {t.notif_type} ({t.param_count} params)
                        </option>
                      ))}
                    </select>
                    {templateType && (() => {
                      const def = filteredCatalog.find((t) => t.notif_type === templateType);
                      const count = def?.param_count || 0;
                      return (
                        <div className="space-y-1.5">
                          {def?.body && (
                            <p
                              className="text-xs text-muted-foreground italic bg-muted/40 rounded p-1.5"
                              data-testid="wa-conv-template-preview"
                            >
                              {def.body}
                            </p>
                          )}
                          {Array.from({ length: count }, (_, i) => i + 1).map((n) => (
                            <Input
                              key={n}
                              value={templateParams[n] || ''}
                              onChange={(e) =>
                                setTemplateParams((p) => ({ ...p, [n]: e.target.value }))
                              }
                              placeholder={`Param {{${n}}}`}
                              data-testid={`wa-conv-template-param-${n}`}
                              className="text-sm h-9 rounded-lg"
                            />
                          ))}
                          <Button
                            onClick={sendReply}
                            disabled={sending}
                            data-testid="wa-conv-template-send"
                            className="w-full rounded-xl"
                          >
                            {sending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-1.5" />
                            )}
                            Send template
                          </Button>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </Card>
  );
};

export default WhatsAppConversationPanel;
